'use client';
/** @jsxImportSource @emotion/react */

import Button from '@egalite/ui/Buttons';
import { css, keyframes } from '@emotion/react';
import { Close, Content, Portal } from '@radix-ui/react-popover';
import { Children, FC, isValidElement, ReactNode } from 'react';
import Icon from '../../Icon';
import NavbarLogo from './NavbarLogo';

const slideInFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(100%);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideOutToRight = keyframes`
  from {
    opacity: 1;
    transform: translateX(0);
  }

  to {
    opacity: 0;
    transform: translateX(100%);
  }
`;

const styles = css`
	will-change: transform, opacity;

	&[data-state='open'] {
		animation: ${slideInFromRight} 400ms ease-out;
	}

	&[data-state='closed'] {
		animation: ${slideOutToRight} 400ms ease-in;
	}
`;

type Props = {
	children?: ReactNode;
};

const NavbarMenuContent: FC<Props> = ({ children }) => {
	return (
		<Portal>
			{/*
				Sem `asChild`: antes o <ul> é que recebia role="dialog" do Radix e tinha
				<div>/<hr>/<a> como filhos diretos — lista inválida. Agora o dialog é o
				próprio Content (com nome via aria-label) e a lista de links fica dentro,
				só com <li>.
			*/}
			<Content
				align="end"
				aria-label="Menu de navegação"
				className="w-max max-h-[100dvh] overflow-y-auto flex md:hidden flex-col items-end gap-5 bg-white px-14 pt-6 pb-32 shadow-xl rounded-l-4xl z-[99999]"
				css={styles}
			>
				<div className="flex gap-20 justify-between w-full">
					<Close asChild>
						<button aria-label="Fechar menu de navegação" className="text-4xl text-[#C6C6C6] hover:text-brand-main transition duration-200">
							<Icon name="close" />
						</button>
					</Close>

					<NavbarLogo />
				</div>

				<hr className="border-[#C6C6C6] w-full my-5" />

				<ul className="flex flex-col items-end gap-5 w-full">
					{Children.map(children, child =>
						isValidElement(child) ? <li className="flex justify-end w-full">{child}</li> : child,
					)}
				</ul>

				<hr className="border-[#C6C6C6] w-full my-5" />

				<Close asChild>
					<Button variant="secondary" size="large" type="link" href="/login" className="w-full" icon={<Icon name="login" />}>
						Login
					</Button>
				</Close>

				<Close asChild>
					<Button size="large" type="link" href="/cadastroempresa" className="w-full" icon={<Icon name="portrait" />}>
						Cadastre-se empresa
					</Button>
				</Close>

				<Close asChild>
					<Button size="large" type="link" href="/candidato/cadastrar" className="w-full" icon={<Icon name="portrait" />}>
						Cadastre-se candidato
					</Button>
				</Close>
			</Content>
		</Portal>
	);
};

export default NavbarMenuContent;
